import { RefObject, useState } from "react";
import useUser from "../../../api/api-hooks/auth/useUser";
import { RecordComment } from "../../../api/api-hooks/comments/useCreateComment";
import { ConversationUsersMap } from "../../../api/api-hooks/comments/useFetchComments";
import IconButton from "../../../elements/IconButton";
import useHover from "../../../hooks/useHover";
import { CommentReactions } from "./CommentReactions";
import CommentOptions from "./CommentOptions";
import StrUtils from "../../../utils/StrUtils";
import ReactionsList from "./ReactionsList";
import AvatarAndName from "./AvatarAndName";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

interface Props {
  comment: RecordComment;
  commentRef: RefObject<HTMLDivElement> | undefined;
  usersMap: ConversationUsersMap;
  handleReaction: ((reaction: string) => void) | undefined;
  handleEdit: (() => void) | undefined;
  handleDelete: (() => void) | undefined;
  replyingTo: RecordComment | null;
  setReplyingTo: React.Dispatch<React.SetStateAction<RecordComment | null>>;
  editComment: RecordComment | null;
  setEditComment: React.Dispatch<React.SetStateAction<RecordComment | null>>;
}

export default function AppComment({
  comment,
  commentRef,
  usersMap,
  handleReaction,
  handleEdit,
  handleDelete,
  replyingTo,
  setReplyingTo,
  editComment,
  setEditComment,
}: Props) {
  const userID = useUser()?.user?.id;

  if (!userID) return null;

  return userID && userID === comment.CreatedBy ? (
    <CommentComponent
      comment={comment}
      commentRef={commentRef}
      usersMap={usersMap}
      replyingTo={replyingTo}
      setReplyingTo={setReplyingTo}
      editComment={editComment}
      setEditComment={setEditComment}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      handleReaction={undefined}
      backgroundColor="#F0F3F2"
      css="justify-content-end ml-4"
    />
  ) : (
    <CommentComponent
      comment={comment}
      commentRef={commentRef}
      usersMap={usersMap}
      handleReaction={handleReaction}
      handleEdit={undefined}
      handleDelete={undefined}
      replyingTo={replyingTo}
      setReplyingTo={setReplyingTo}
      editComment={editComment}
      setEditComment={setEditComment}
      backgroundColor="#FFFFFF"
      css="justify-content-start mr-4"
    />
  );
}

interface CommentProps extends Props {
  backgroundColor: string;
  css: string;
}

function CommentComponent({
  comment,
  commentRef,
  usersMap,
  handleReaction,
  replyingTo,
  setReplyingTo,
  editComment,
  setEditComment,
  handleEdit,
  handleDelete,
  backgroundColor,
  css,
}: CommentProps) {
  const user = usersMap?.[comment?.CreatedBy || ""];
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();
  const [menuOpen, setMenuOpen] = useState(false);
  const isBeingRepliedTo = replyingTo?._id === comment?._id;
  const isBeingEdited = editComment?._id === comment?._id;
  const isDeleted = !!comment?.Deleted;

  function handleCopy() {
    navigator.clipboard.writeText(
      StrUtils.convertMarkupToPlaintext(comment?.Comment?.RichText)
    );
  }

  return (
    <div className={`tw:flex tw:p-3 ${css}`} id={comment._id} ref={commentRef}>
      <div
        ref={hoverRef}
        className={`rounded btn-oauth py-2 px-3 position-relative ${
          isBeingRepliedTo || isBeingEdited ? "bg-primary-selected" : ""
        }`}
        style={{
          backgroundColor:
            isBeingRepliedTo || isBeingEdited ? undefined : backgroundColor,
          maxWidth: 650,
          width: "100%",
        }}
      >
        <div className="tw:flex tw:items-center tw:justify-between">
          <AvatarAndName user={user} />
          <div
            className="tw:flex mt--3"
            style={{
              height: 20,
              opacity: isHovered || menuOpen ? 100 : 0,
            }}
          >
            {handleReaction && !isDeleted && (
              <CommentReactions
                setMenuOpen={setMenuOpen}
                handleReaction={handleReaction}
              />
            )}
            {!isDeleted && (
              <IconButton
                icon="reply"
                onClick={() => {
                  setEditComment(null);
                  setReplyingTo(comment);
                }}
                className="ml-0 mr-0"
              />
            )}
            <CommentOptions
              handleEdit={isDeleted ? undefined : handleEdit}
              handleCopy={isDeleted ? undefined : handleCopy}
              handleDelete={comment?.Deleted ? undefined : handleDelete}
              handleRestore={comment?.Deleted ? handleDelete : undefined}
              setMenuOpen={setMenuOpen}
            />
          </div>
        </div>
        <ParentComment comment={comment} usersMap={usersMap} />
        <div
          className="custom-quill-text text-dark mt-1"
          style={{ wordBreak: "break-all" }}
          dangerouslySetInnerHTML={{
            __html: comment.Deleted
              ? "<p><em>This message was deleted</em></p>"
              : comment.Comment.RichText,
          }}
        ></div>
        <div className="d-flex justify-content-end text-medium text-xs">
          {comment?.Created &&
            dayjs(comment.Created).format("MMM Do YYYY, h:mm a")}
        </div>
        <div
          className="position-absolute d-flex gap-1"
          style={{ bottom: -11, left: 18 }}
        >
          <ReactionsList
            comment={comment}
            handleReaction={handleReaction}
            usersMap={usersMap}
          />
        </div>
      </div>
    </div>
  );
}

function ParentComment({
  comment,
  usersMap,
}: {
  comment: RecordComment;
  usersMap: ConversationUsersMap;
}) {
  if (!comment?.ParentComment) return null;

  return (
    <div
      className="my-2 rounded-sm btn-oauth py-2 px-3 bg-white cursor-pointer"
      onClick={() =>
        document
          .getElementById(comment?.ParentComment?._id as string)
          ?.scrollIntoView({ behavior: "smooth" })
      }
    >
      <h5 className="mb-1">
        {usersMap?.[comment.ParentComment?.CreatedBy]?._RecordName ||
          "Deleted User"}
      </h5>
      <div className="text-truncate text-dark text-sm">
        {comment.ParentComment?.Comment}
      </div>
    </div>
  );
}
